import { useState } from 'react';
import { DevicePhoneMobileIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';

interface WebsitePreviewProps {
  html: string;
}

export default function WebsitePreview({ html }: WebsitePreviewProps) {
  const [mode, setMode] = useState<'desktop' | 'mobile'>('desktop');

  return (
    <div className="space-y-3">
      {/* Toggle bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-charcoal-400 font-medium">Preview Mode</p>
        <div className="inline-flex rounded-lg border border-charcoal-200 bg-charcoal-50 p-0.5">
          <button
            onClick={() => setMode('desktop')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              mode === 'desktop'
                ? 'bg-white text-copper-600 shadow-sm'
                : 'text-charcoal-400 hover:text-charcoal-600'
            }`}
          >
            <ComputerDesktopIcon className="h-4 w-4" />
            Desktop
          </button>
          <button
            onClick={() => setMode('mobile')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              mode === 'mobile'
                ? 'bg-white text-copper-600 shadow-sm'
                : 'text-charcoal-400 hover:text-charcoal-600'
            }`}
          >
            <DevicePhoneMobileIcon className="h-4 w-4" />
            Mobile
          </button>
        </div>
      </div>

      {/* Preview frame */}
      <div className={`
        mx-auto transition-all duration-300 ease-in-out
        ${mode === 'mobile' ? 'max-w-[375px]' : 'w-full'}
      `}>
        <div className={`
          bg-white rounded-2xl overflow-hidden border-2 transition-all
          ${mode === 'mobile'
            ? 'border-charcoal-300 shadow-xl'
            : 'border-charcoal-200 shadow-md'
          }
        `}>
          {/* Phone notch (mobile only) */}
          {mode === 'mobile' && (
            <div className="bg-charcoal-800 h-7 flex items-center justify-center">
              <div className="w-20 h-4 bg-charcoal-900 rounded-b-xl" />
            </div>
          )}

          {/* Browser bar (desktop only) */}
          {mode === 'desktop' && (
            <div className="bg-charcoal-100 h-8 flex items-center px-3 gap-1.5 border-b border-charcoal-200">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
              <div className="flex-1 mx-3 bg-white rounded h-5 px-2 flex items-center">
                <span className="text-[10px] text-charcoal-400">yourpractice.com</span>
              </div>
            </div>
          )}

          {/* Content */}
          <div className={`
            overflow-auto
            ${mode === 'mobile' ? 'max-h-[600px]' : 'max-h-[500px]'}
          `}>
            <div
              className="prose prose-sm max-w-none p-4
                prose-headings:text-charcoal-700
                prose-h3:text-copper-600 prose-h3:text-lg prose-h3:font-bold
                prose-p:text-charcoal-600 prose-p:leading-relaxed
                prose-li:text-charcoal-600
                prose-strong:text-charcoal-700
                prose-a:text-copper-600
              "
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>

          {/* Phone home bar (mobile only) */}
          {mode === 'mobile' && (
            <div className="bg-charcoal-50 h-5 flex items-center justify-center border-t border-charcoal-100">
              <div className="w-24 h-1 bg-charcoal-300 rounded-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
